// src/lib/contractErrors.js — ONE place that turns a contract refusal into words an agent can act
// on (owner 2026-09-19: the form showed "payment_method", "initial_completion_rule",
// "transaction_reference_required", "invalid_payment_rules" to people who fill contracts).
//
// Pattern: WHAT IS WRONG + WHAT TO DO, naming the Georgian field label, never the schema key.
// Every field the server names becomes an item the page can scroll to (`key` = the form's
// data-field). The technical code survives only as `code`, for a collapsed "details" line.
// Unknown refusals say "შენახვა ვერ მოხერხდა. სცადეთ ხელახლა." — never the raw code.
// Pure (the caller passes the draft's fields and the language); tested by contractErrors.test.mjs.
import { refusalSentence } from './contractChecks.js'
import { paymentMessage } from './rentalPaymentRoutes.js'

const W = {
  ka: {
    generic: 'შენახვა ვერ მოხერხდა. სცადეთ ხელახლა.',
    fill_n: n => `ხელშეკრულების დასასრულებლად შეავსეთ ${n} საკითხი:`,
    fix_n: n => `ხელშეკრულების დასასრულებლად გაასწორეთ ${n} საკითხი:`,
    fill: label => `შეავსეთ: ${label}`,
    check: label => `შეამოწმეთ: ${label}`,
    network: 'სერვერთან კავშირი ვერ დამყარდა. შეამოწმეთ ინტერნეტი და სცადეთ ხელახლა.',
    system: ref => `ხელშეკრულება ვერ შეინახა სისტემის შეცდომის გამო — თქვენს მონაცემებში შეცდომა არ არის და ფორმა შენახულია. ხელახლა ცდა არ უშველის: შეატყობინეთ ადმინისტრატორს${ref ? ` კოდი ${ref}` : ''}.`,
  },
  en: {
    generic: 'Could not save. Please try again.',
    fill_n: n => `To finish the contract, complete ${n} item${n === 1 ? '' : 's'}:`,
    fix_n: n => `To finish the contract, correct ${n} item${n === 1 ? '' : 's'}:`,
    fill: label => `Fill in: ${label}`,
    check: label => `Check: ${label}`,
    network: 'Could not reach the server. Check the connection and try again.',
    system: ref => `The contract could not be saved because of a system fault — nothing you entered is wrong and your form is kept. Trying again will not help: report it to the administrator${ref ? ` with code ${ref}` : ''}.`,
  },
  ru: {
    generic: 'Не удалось сохранить. Попробуйте ещё раз.',
    fill_n: n => `Чтобы завершить договор, заполните пунктов: ${n}`,
    fix_n: n => `Чтобы завершить договор, исправьте пунктов: ${n}`,
    fill: label => `Заполните: ${label}`,
    check: label => `Проверьте: ${label}`,
    network: 'Не удалось связаться с сервером. Проверьте подключение и попробуйте ещё раз.',
    system: ref => `Договор не сохранён из-за системной ошибки — в ваших данных ошибки нет, форма сохранена. Повторная попытка не поможет: сообщите администратору${ref ? ` код ${ref}` : ''}.`,
  },
}
const words = lang => W[lang] || W.ka
const L3 = (lang, o) => o[lang] || o.ka

// 👤 WHICH PARTY, WHICH BOX (owner 2026-09-23: "exact detailed explanation what needs to be filled and how").
// The server names the party (`role`, `missing`, `parties`); the form anchors each box as party:<role>:<box>.
const ROLE = {
  landlord: { ka: 'გამქირავებელი', en: 'Landlord', ru: 'Арендодатель' },
  tenant: { ka: 'დამქირავებელი', en: 'Tenant', ru: 'Арендатор' },
  owner: { ka: 'მესაკუთრე', en: 'Owner', ru: 'Собственник' },
  client: { ka: 'კლიენტი', en: 'Client', ru: 'Клиент' },
  agency: { ka: 'სააგენტო', en: 'Agency', ru: 'Агентство' },
  cobroker: { ka: 'პარტნიორი ბროკერი', en: 'Co-operating broker', ru: 'Сотрудничающий брокер' },
  resident: { ka: 'მაცხოვრებელი', en: 'Resident', ru: 'Проживающий' },
}
const BOX = {
  legal_name: { anchor: 'name', ka: '„სახელი, გვარი — როგორც პირადობაშია“ ცარიელია — შეიყვანეთ სრული სახელი და გვარი ზუსტად ისე, როგორც დოკუმენტშია.',
    en: '"Name, surname — as on the ID" is empty — enter the full name exactly as written on the document.',
    ru: '«Имя, фамилия — как в документе» не заполнено — введите полное имя точно как в документе.' },
  id_number: { anchor: 'id', ka: 'აირჩიეთ დოკუმენტი და შეიყვანეთ მისი ნომერი — პირადი ნომერი 11 ციფრია; პასპორტის ნომერი — როგორც პასპორტშია.',
    en: 'Choose the document and enter its number — a personal number has 11 digits; a passport number as printed in the passport.',
    ru: 'Выберите документ и введите его номер — личный номер состоит из 11 цифр; номер паспорта — как в паспорте.' },
  id_number_invalid: { anchor: 'id', ka: 'დოკუმენტის ნომერი არასწორია — პირადი ნომერი ზუსტად 11 ციფრია; პასპორტის ან კომპანიის ნომერი 4-დან 20 სიმბოლომდეა, ზუსტად ისე, როგორც დოკუმენტშია.',
    en: 'The document number is not valid — a personal number is exactly 11 digits; a passport or company number is 4–20 characters, exactly as on the document.',
    ru: 'Номер документа неверный — личный номер ровно 11 цифр; номер паспорта или компании 4–20 символов, точно как в документе.' },
  id_kind_invalid: { anchor: 'id', ka: 'აირჩიეთ დოკუმენტის ტიპი სიიდან: პირადობის მოწმობა, პასპორტი ან კომპანია.',
    en: 'Choose the document type from the list: ID card, passport or company.', ru: 'Выберите тип документа из списка: удостоверение, паспорт или компания.' },
  rep_name: { anchor: 'rep', ka: 'კომპანიისთვის შეიყვანეთ ხელმომწერი წარმომადგენლის სახელი და გვარი.',
    en: 'For a company, enter the name of the representative who signs.', ru: 'Для компании введите имя и фамилию подписывающего представителя.' },
  phone: { anchor: 'phone', ka: '„ტელეფონი“ — მხოლოდ ციფრები (დასაწყისში +, ჰარი და ტირე დასაშვებია), მინიმუმ 7 ციფრი, მაგ. +995 591 32 86 86.',
    en: '"Phone" — digits only (a leading +, spaces and dashes are fine), at least 7 digits, e.g. +995 591 32 86 86.',
    ru: '«Телефон» — только цифры (допустимы + в начале, пробелы и дефисы), не менее 7 цифр, напр. +995 591 32 86 86.' },
  email: { anchor: 'email', ka: '„ელფოსტა“ — შეიყვანეთ სრული მისამართი, მაგ. name@example.com.',
    en: '"Email" — enter a full address, e.g. name@example.com.', ru: '«Эл. почта» — введите полный адрес, напр. name@example.com.' },
  party_kind_invalid: { anchor: 'name', ka: 'მხარის ბლოკში აირჩიეთ „ფიზიკური პირი“ ან „კომპანია“.',
    en: 'In the party block choose "Person" or "Company".', ru: 'В блоке стороны выберите «Физическое лицо» или «Компания».' },
}
const CODE_BOX = { party_name_required: ['legal_name'], id_number_invalid: ['id_number_invalid'], id_kind_invalid: ['id_kind_invalid'],
  phone_invalid: ['phone'], email_invalid: ['email'], party_kind_invalid: ['party_kind_invalid'] }
function partyItems(code, body, lang) {
  const out = []
  // 🪑 a co-owner is named by its seat (`seat` in the refusal) — anchor party:owner#2:…, label „მესაკუთრე 2"
  const push = (role, box, seat) => {
    const b = BOX[box]
    if (!b || !role) return
    const n = Number(seat) > 1 ? Number(seat) : 1
    out.push({ key: `party:${n > 1 ? `${role}#${n}` : role}:${b.anchor}`, label: `${L3(lang, ROLE[role] || { ka: role })}${n > 1 ? ` ${n}` : ''}: ${L3(lang, b)}` })
  }
  if (Array.isArray(body.parties)) { for (const p of body.parties) for (const m of (p?.missing || [])) push(p.role, m, p.seat) }
  else if (body.role && CODE_BOX[code]) { for (const m of CODE_BOX[code]) push(body.role, m, body.seat) }
  else if (body.role && Array.isArray(body.missing)) { for (const m of body.missing) push(body.role, m, body.seat) }
  return out
}

// the server's route guards answer in words, not codes — each gets a sentence that says what to do
const GUARD = [
  [/^meeting not found$/i, { ka: 'შეხვედრა ვერ მოიძებნა ან წაშლილია — გახსენით ხელშეკრულება მოქმედი შეხვედრიდან.', en: 'The meeting was not found or has been deleted — open the contract from an active meeting.', ru: 'Встреча не найдена или удалена — откройте договор из действующей встречи.' }],
  [/^not found$/i, { ka: 'ხელშეკრულება ვერ მოიძებნა — გახსენით იგი „ხელშეკრულებების“ სიიდან.', en: 'The contract was not found — open it from the Contracts list.', ru: 'Договор не найден — откройте его из списка договоров.' }],
  [/^template not found$/i, { ka: 'ხელშეკრულების ტიპი ვერ მოიძებნა — დაბრუნდით და აირჩიეთ ტიპი ხელახლა.', en: 'The contract type was not found — go back and choose the type again.', ru: 'Тип договора не найден — вернитесь и выберите тип заново.' }],
  [/^property not found$/i, { ka: 'ქონება ვერ მოიძებნა — გახსენით ხელშეკრულება შეხვედრიდან ან ქონების ბარათიდან.', en: 'The property was not found — open the contract from a meeting or the property card.', ru: 'Объект не найден — откройте договор из встречи или карточки объекта.' }],
  [/context required/i, { ka: 'ხელშეკრულება უნდა გაიხსნას შეხვედრიდან ან ქონების ბარათიდან.', en: 'Open the contract from a meeting or a property card.', ru: 'Откройте договор из встречи или карточки объекта.' }],
  [/not allowed to act/i, { ka: 'ამაზე მოქმედების უფლება არ გაქვთ — მიმართეთ მენეჯერს ან შეხვედრის აგენტს.', en: "You are not allowed to act on this — ask your manager or the meeting's agent.", ru: 'У вас нет права на это действие — обратитесь к менеджеру или агенту встречи.' }],
]

// codes whose field list means "unanswered" rather than "wrong"
const MISSING = new Set(['missing_fields', 'identity_required', 'party_name_required'])
// codes whose own sentence explains the list, so it LEADS the listed fields instead of "correct N items"
const LEADS_LIST = new Set(['translation_required', 'override_needs_reason'])

// the payment calendar refuses in English sentences (server payment_calendar.js), not codes
const CALENDAR = [
  [/^invalid rental date$/i, 'calendar_invalid_date'],
  [/^invalid payment amount or currency$/i, 'calendar_invalid_amount'],
  [/^invalid rental payment terms$/i, 'calendar_invalid_terms'],
  [/^first regular payment date outside tenancy$/i, 'first_recurring_payment_date'],
  [/exceeds 240 periods/i, 'calendar_too_long'],
  [/^invalid rent-free period$/i, 'rent_free_months'],
  [/^rent-free period leaves no paid rent$/i, 'rent_free_leaves_no_rent'],
  [/^prepaid month outside tenancy$/i, 'prepaid_month_outside_tenancy'],
  [/^duplicate prepaid month$/i, 'duplicate_prepaid_month'],
  [/^prepaid month is rent-free$/i, 'prepaid_month_rent_free'],
  [/^prepaid amount exceeds rent$/i, 'prepay_paid_exceeds_rent'],
  [/^partial rent components do not reconcile$/i, 'prepay_remaining_mismatch'],
  [/^prior first-rent credit exceeds initial allocation$/i, 'prior_credit_exceeds_first_month'],
  [/^prior deposit credit exceeds deposit$/i, 'prior_credit_exceeds_deposit'],
]
// receipts: the three evidence boxes are named the same on every receipt refusal
const EVIDENCE_CODES = new Set(['invalid_tenant_agency_fee_receipt', 'verified_initiation_required', 'verified_credit_required'])

/** The sentence for the PART the refusal names, or null.
 *  🎯 FOR EVERY CODE (owner 2026-09-29, "fix it on every error"): a refusal carrying `field` or `detail`
 *  is said as `<code>_<field|detail>` whenever that sentence exists. It used to be done for three codes
 *  only, so Irakli's lease refusal naming the missing fee model (invalid_commercial_policy /
 *  agency_fee_model) showed only "incomplete or invalid". Wins over the page's own general sentence too. */
function payloadSentence(code, body, lang) {
  let specific = null
  for (const part of [body.field, body.detail]) {
    if (specific || typeof part !== 'string' || !part) continue
    specific = refusalSentence(`${code}_${part}`, lang)
      || (EVIDENCE_CODES.has(code) ? refusalSentence(`evidence_${part}`, lang) : null)
  }
  if (!specific && code === 'invalid_payment_calendar' && typeof body.detail === 'string') {
    const hit = CALENDAR.find(([re]) => re.test(body.detail.trim()))
    if (hit) specific = refusalSentence(hit[1], lang)
  }
  if (code === 'notice_channels_required' && Array.isArray(body.sides) && body.sides.length) {
    const sides = [...new Set(body.sides.map(String))].sort().join('_')
    specific = refusalSentence(`notice_channels_required_${sides}`, lang) || specific
  }
  // the fee the commercial terms require, said as the amount (2026-09-24)
  if (code === 'owner_fee_mismatch' && body.expected?.value != null)
    specific = (refusalSentence('owner_fee_mismatch_expected', lang) || '').replace('{amount}', `${body.expected.value} ${body.expected.currency || ''}`.trim()) || specific
  return specific
}

/** The sentence for a refusal, made specific by its payload where the server says more than the code. */
function codeSentence(code, body, lang) {
  return payloadSentence(code, body, lang) || refusalSentence(code, lang)
}

const fieldLabel = (fields, key, lang) => {
  const f = (fields || []).find(x => x && x.key === key)
  if (!f) return null
  return (lang === 'en' ? f.label_en || f.label_ka : f.label_ka || f.label_en) || null
}

/** One sentence for one field the server named, or null when nothing specific is known. */
function fieldSentence(code, field, fields, lang) {
  const w = words(lang)
  const specific = paymentMessage(field, lang)
    || (code === 'invalid_remote_rental' ? refusalSentence(`remote_${field}`, lang) : null)
  if (specific) return specific
  const label = fieldLabel(fields, field, lang)
  if (!label) return null
  return MISSING.has(code) ? w.fill(label) : w.check(label)
}

// value_out_of_range carries each field's limits in `detail` ({key,min,max,value} or {key,after,value})
const RANGE = {
  ka: { between: (l, a, b) => `${l}: უნდა იყოს ${a}-დან ${b}-მდე`, min: (l, a) => `${l}: მინიმუმ ${a}`, max: (l, b) => `${l}: მაქსიმუმ ${b}`, after: (l, a) => `${l}: უნდა იყოს „${a}"-ის შემდეგ` },
  en: { between: (l, a, b) => `${l}: must be from ${a} to ${b}`, min: (l, a) => `${l}: at least ${a}`, max: (l, b) => `${l}: at most ${b}`, after: (l, a) => `${l}: must be after "${a}"` },
  ru: { between: (l, a, b) => `${l}: должно быть от ${a} до ${b}`, min: (l, a) => `${l}: не менее ${a}`, max: (l, b) => `${l}: не более ${b}`, after: (l, a) => `${l}: должно быть после «${a}»` },
}
function rangeSentence(code, key, body, fields, lang) {
  if (code !== 'value_out_of_range' || !Array.isArray(body.detail)) return null
  const d = body.detail.find(x => x && x.key === key)
  const label = fieldLabel(fields, key, lang)
  if (!d || !label) return null
  const r = RANGE[lang] || RANGE.ka
  if (d.after) { const a = fieldLabel(fields, d.after, lang); return a ? r.after(label, a) : null }
  if (d.min != null && d.max != null) return r.between(label, d.min, d.max)
  if (d.min != null) return r.min(label, d.min)
  if (d.max != null) return r.max(label, d.max)
  return null
}

/**
 * @param {Error & {body?: object, status?: number}} e  a coreApi error (message = the server's code)
 * @param {{fields?: object[], lang?: 'ka'|'en', known?: (code) => string|null}} opts
 *        `known` — the page's own sentences for page-level codes (signed, lang_not_reviewed, …)
 * @returns {{title: string, items: {key: string|null, label: string}[], code: string|null}}
 */
export function describeContractError(e, { fields = [], lang = 'ka', known = null } = {}) {
  const w = words(lang)
  const code = e?.message ? String(e.message) : null
  const body = e?.body && typeof e.body === 'object' ? e.body : {}
  if (!code || /failed to fetch|networkerror|load failed/i.test(code)) return { title: w.network, items: [], code }
  // a system fault, or raw text from a 5xx: never the agent's mistake, never "try again"
  if (code === 'system_fault' || ((e?.status || 0) >= 500 && !/^[a-z][a-z0-9_]*$/.test(code))) return { title: w.system(body.ref || ''), items: [], code }
  const guard = GUARD.find(([re]) => re.test(code))
  if (guard) return { title: L3(lang, guard[1]), items: [], code }
  if (code === 'void') return { title: refusalSentence('contract_void', lang) || w.generic, items: [], code }
  const party = partyItems(code, body, lang)
  if (party.length) return { title: party.length === 1 ? party[0].label : w.fill_n(party.length), items: party, code }
  const keys = [...new Set([...(Array.isArray(body.fields) ? body.fields : []), ...(body.field ? [body.field] : [])].map(String))]
  const items = keys.map(k => ({ key: k, label: rangeSentence(code, k, body, fields, lang) || fieldSentence(code, k, fields, lang) })).filter(x => x.label)
  if (LEADS_LIST.has(code) && items.length) return { title: codeSentence(code, body, lang), items, code }
  if (items.length > 1 || (items.length === 1 && MISSING.has(code)))
    return { title: (MISSING.has(code) ? w.fill_n : w.fix_n)(items.length), items, code }
  // the named part beats the page's general sentence for the code (2026-09-29): the page knows
  // "the advance amounts do not add up", the payload knows WHICH amount and why
  const pageSaid = typeof known === 'function' ? known(code) : null
  const said = payloadSentence(code, body, lang) || pageSaid || refusalSentence(code, lang)
  if (items.length === 1) return { title: said || items[0].label, items: said ? items : [], code }
  return { title: said || w.generic, items: [], code }
}

/** A sentence for a code on the side panels (remote payments, tenant fee, annex) — never the raw code.
 *  `body` (the error's payload, optional) makes it specific where the server says more than the code. */
export function refusalOrGeneric(code, lang = 'ka', body = null) {
  return codeSentence(String(code || ''), body && typeof body === 'object' ? body : {}, lang) || words(lang).generic
}
